<template>
  <EmojiPicker :search="search" class="emoji-picker-container" :class="{'no-animation': noAnimation}">
    <div class="emoji-invoker" slot="emoji-invoker" slot-scope="{ events: { click: clickEvent } }" @click.stop="clickEvent">
      <VcIcon class="emoji-invoker__icon" @click="clearSearch" data-qa="emoji-invoker" :size="activatorIconSize">{{ activatorIcon }}</VcIcon>
    </div>
    <div slot="emoji-picker" slot-scope="{ emojis, insert, display }">
      <div class="emoji-picker" :class="{'pane-view': isSmallScreen}">
        <div class="emoji-picker__header">
          <div class="emoji-picker__search">
<!--            we not using Design System by product and designer choice -->
            <v-text-field v-model="search"
                          :label="locales.search"
                          data-qa="emoji-search"
                          prepend-inner-icon="icon-search"
                          hide-details single-line dense outlined />
          </div>
          <div v-if="skinToneFeatureEnabled && !isEmpty(emojis)" class="emoji-picker__skin-tone">
            <v-btn plain
                   icon
                   v-for="tone in skinTones"
                   :class="{selected: preferredSkinTone === tone}" class="none-skin-tone"
                   @click="setSkinTone(tone)">{{tone}}</v-btn>
          </div>
          <v-tabs centered class="emoji-picker__tabs" v-model="activeTab" v-if="!search && !isEmpty(emojis)">
            <v-tab v-for="(group, category, idx) in emojis" :key="category">
              <v-tooltip top>
                <template v-slot:activator="{ on, attrs }">
                  <v-icon size="20"
                          v-bind="attrs"
                          v-on="on">
                    {{locales.categories[category.toLowerCase()].icon}}
                  </v-icon>
                </template>
                <span>{{ locales.categories[category.toLowerCase()].text }}</span>
              </v-tooltip>
            </v-tab>
          </v-tabs>
        </div>
        <div class="emoji-picker__empty" v-if="isEmpty(emojis)">
          {{locales.notfound}}
        </div>
        <div v-else-if="search" class="emoji-picker__search-group">
          <template v-for="(emojiGroup) in emojis">
            <EmojiItem v-for="(emoji, emojiName) in emojiGroup"
                       :emoji="skinToneFeatureEnabled && isSkinsToneApplicable(emoji) ? skinToneEmoji(emoji) : emoji"
                       :emoji-name="emojiName.split('_').join(' ')"
                       @click="selectEmoji(emoji, emojiName)" />
          </template>
        </div>
        <v-tabs-items v-else v-model="activeTab">
          <v-tab-item class="emoji-picker__tab">
            <EmojiItem v-for="({emoji, emojiName}) in lastUsedEmoji"
                       v-if="!isEmpty(emojis)"
                       :emoji="skinToneFeatureEnabled && isSkinsToneApplicable(emoji) ? skinToneEmoji(emoji) : emoji"
                       :emoji-name="emojiName.split('_').join(' ')"
                       @click="selectEmoji(emoji, emojiName, false)"/>
          </v-tab-item>
          <v-tab-item v-for="(emojiGroup, groupName) in emojis"
                      class="emoji-picker__tab"
                      v-if="groupName !== 'Frequently used'">
              <EmojiItem v-for="(emoji, emojiName) in emojiGroup"
                         :emoji="skinToneFeatureEnabled && isSkinsToneApplicable(emoji) ? skinToneEmoji(emoji) : emoji"
                         :emoji-name="emojiName.split('_').join(' ')"
                         @click="selectEmoji(emoji, emojiName)" />
          </v-tab-item>
        </v-tabs-items>
      </div>
    </div>
  </EmojiPicker>
</template>

<script>
import EmojiPicker from 'vue-emoji-picker';
import EmojiItem from "@/modules/common/components/VcitaEmojiPicker/EmojiItem";

const applicableEmojiForSkinTone = new Set([0x261D, 0x26F9,0x270A, 0x270B,0x270C, 0x270D,0x1F385, 0x1F3C3, 0x1F3C4, 0x1F3CA, 0x1F3CB, 0x1F442, 0x1F443, 0x1F446, 0x1F447, 0x1F448, 0x1F449, 0x1F44A, 0x1F44B, 0x1F44C, 0x1F44D, 0x1F44E, 0x1F44F, 0x1F450, 0x1F466, 0x1F467, 0x1F468, 0x1F469, 0x1F46E, 0x1F470, 0x1F471, 0x1F472, 0x1F473, 0x1F474, 0x1F475, 0x1F476, 0x1F477, 0x1F478, 0x1F47C, 0x1F481, 0x1F482, 0x1F483, 0x1F485, 0x1F486, 0x1F487, 0x1F4AA, 0x1F575, 0x1F57A, 0x1F590, 0x1F595, 0x1F596, 0x1F645, 0x1F646, 0x1F647, 0x1F64B, 0x1F64C, 0x1F64D, 0x1F64E, 0x1F64F, 0x1F6A3, 0x1F6B4, 0x1F6B5, 0x1F6B6, 0x1F6C0, 0x1F918, 0x1F919, 0x1F91A, 0x1F91B, 0x1F91C, 0x1F91D, 0x1F91E, 0x1F926, 0x1F930, 0x1F933, 0x1F934, 0x1F935, 0x1F936, 0x1F937, 0x1F938, 0x1F939, 0x1F93C, 0x1F93D, 0x1F93E])

export default {
  name: 'VcitaEmojiPicker',
  components: {EmojiItem, EmojiPicker },
  props:{
    activatorIcon: {
      type: String,
      default: 'icon-smile',
      required: false
    },
    activatorIconSize: {
      type: Number,
      default: 16,
      required: false
    },
    isSmallScreen: {
      type: Boolean,
      default: false
    },
    noAnimation: {
      type: Boolean,
      default: false
    }
  },
  data() {
    return {
      skinToneFeatureEnabled: false,
      search: '',
      activeTab: 1,
      preferredSkinTone: localStorage.getItem('preferredSkinTone') || '',
      skinTones: ['', '🏻', '🏼', '🏽', '🏾', '🏿'],
      lastUsedEmoji: JSON.parse(localStorage.getItem('lastUsedEmoji')) || [{emoji: '😀', emojiName: 'grinning'}],
    }
  },
  computed: {
    locales() {
      return {
        search: this.$t('common.emojis.search'),
        notfound: this.$t('common.emojis.notfound'),
        categories: {
          'frequently used': {
            icon: 'icon-Time',
            text: this.$t('common.emojis.categories.recent'),
          },
          people: {
            icon: 'icon-smile',
            text: this.$t('common.emojis.categories.people'),
          },
          nature: {
            icon: 'icon-leaf',
            text: this.$t('common.emojis.categories.nature'),
          },
          objects: {
            icon: 'icon-basketball-ball',
            text: this.$t('common.emojis.categories.objects'),
          },
          places: {
            icon: 'icon-car',
            text: this.$t('common.emojis.categories.places'),
          },
          symbols: {
            icon: 'icon-flag',
            text: this.$t('common.emojis.categories.symbols'),
          },
        }
      }
    },
  },
  methods: {
    clearSearch() {
      this.search = ''
    },
    setSkinTone(value) {
      this.preferredSkinTone = value
      localStorage.setItem('preferredSkinTone', value)
    },
    skinToneEmoji(emoji) {
      return emoji + this.preferredSkinTone
    },
    isSkinsToneApplicable(emoji) {
      return applicableEmojiForSkinTone.has(emoji.toString().codePointAt(0))
    },
    selectEmoji(emoji, emojiName, saveLast = true) {
      saveLast && this.setLastUsedEmoji(emoji, emojiName)
      if(this.isSkinsToneApplicable(emoji)) {
        emoji = this.skinToneEmoji(emoji);
      }
      this.$emit('emoji-selected', emoji)
    },
    isEmpty(obj) {
      return Object.keys(obj).length === 0
    },
    setLastUsedEmoji(emoji, emojiName) {
      if(this.lastUsedEmoji.some(usedEmoji => usedEmoji.emoji === emoji)) return

      if(this.lastUsedEmoji.length > 20) {
        this.lastUsedEmoji.pop()
      }
      this.lastUsedEmoji.unshift({emoji, emojiName});

      localStorage.setItem('lastUsedEmoji', JSON.stringify(this.lastUsedEmoji))
    }
  }
}
</script>

<style lang="scss" scoped>
@import "@/assets/scss/abstract/_variables.scss";

.emoji-picker-container {
  position: relative;
}
.emoji-invoker {
  border-radius: 50%;
  cursor: pointer;
  transition: all 0.2s;
  margin-inline-end: 0;

  &:hover {
    transform: scale(1.2);
  }

  .no-animation & {
    transform: scale(1) !important;
    border-radius: unset;
    &__icon {
      padding: 8px;
      width: 32px;
      height: 32px;

      &:after {
        border-radius: unset;
        transform: scale(1);
      }
    }
  }
}

.emoji-picker {
  position: absolute;
  z-index: 10;
  border: 1px solid #ccc;
  width: 300px;
  height: 348px;
  box-sizing: border-box;
  border-radius: 10px;
  background: #fff;
  box-shadow: 0 1px 10px rgba(0, 0, 0, 0.12), 0 4px 7px rgba(0, 0, 0, 0.14), 0 2px 4px -1px rgba(0, 0, 0, 0.2);
  inset-block: -354px auto;
  overflow-y: auto;
  display: flex;
  flex-direction: column;

   &.pane-view {
     inset-inline: auto -244px;
   }

  &__empty {
    align-self: center;
  }

  &__search {
    display: flex;
    padding: 10px 10px 0;

    ::v-deep{
      fieldset {
        &:hover {
          color: $grey-color-17;
        }
      }
      .v-text-field--outlined.v-input--is-focused fieldset {
        border-color: var(--brand-color-secondary);
      }
    }

    &-group {
      padding: 7px;
    }
  }

  ::v-deep {
    .v-slide-group__wrapper {
      box-shadow: none;
      border-bottom: 1px solid $input-border;
    }
    .v-tabs-slider-wrapper {
      color: $brand_secondary_color;
    }
  }

  &__tabs {
    height: 48px;

    .v-tab {
      width: 47px;
      height: 48px;
      margin: 0;
      min-width: unset;

      &:before {
        color: $black-color-1;
      }
    }
    ::v-deep .v-tabs-bar {
      height: 48px;
    }
  }

  &__tab {
    display: flex;
    flex-wrap: wrap;
    align-content: baseline;
    padding: 7px;
    justify-content: flex-start;
  }

  &__skin-tone {
    .v-btn {
      border-radius: 0;
      margin: 5px;
      padding: 2px;
      width: 14px;
      height: 16px;
    }
    .none-skin-tone {
      background-color: yellow;
      border: 1px solid lightgoldenrodyellow;
    }
    .selected {
      border: 2px solid black
    }
  }
}

.general-tabs .v-window {
  overflow: initial;
  height: unset;
  padding: 0;
  overflow-y: scroll;
}

::v-deep {
  .v-slide-group__prev--disabled, .v-slide-group__prev, .v-slide-group__next {
    display: none !important;
  }
}
</style>
